"use client"

import React, { FC, useState } from "react"
import type { NovaSession } from "@/types"
import { FragmentType, graphql, useFragment } from "@/gql"
import { ampli } from "@/ampli"
import { DocumentNode } from "graphql"
import { ApplyToJobDrawer } from "./ApplyToJob/ApplyToJobDrawer"
import { JobApplicationDrawer } from "./JobApplication/JobApplicationDrawer"
import { AuthenticatedJobDetailsFooter } from "./JobDetails/JobDetailsAuthenticated/AuthenticatedJobDetailsFooter"
import { AuthenticatedJobDetailsContent } from "./JobDetails/JobDetailsAuthenticated/AuthenticatedJobsDetailsContent"

const JobDetailJobPostingFragment = graphql(`
  fragment JobDetailJobPosting on JobPosting {
    id
    __typename

    position {
      id
    }

    ...AuthenticatedJobDetailsContentJobPosting
    ...AuthenticatedJobDetailsFooterCreativeViewer
  }
`)

interface Props {
  serverSession: NovaSession
  jobPostingHasImage?: boolean

  withdrawMutationRefetchQueries: DocumentNode[]
  loading: boolean
  jobPosting: FragmentType<typeof JobDetailJobPostingFragment> | undefined | null
  openSubscriptionCheckoutModal: (jobId: string) => void
}

export const JobDetail: FC<Props> = ({
  serverSession,
  loading,
  jobPostingHasImage = false,
  withdrawMutationRefetchQueries,
  openSubscriptionCheckoutModal,
  ...props
}) => {
  const jobPosting = useFragment(JobDetailJobPostingFragment, props.jobPosting)

  const [applyModal, setApplyModal] = useState<{
    open: boolean
    jobPostingId: string | null
    positionId: string | null
  }>({
    open: false,
    jobPostingId: null,
    positionId: null,
  })

  const [applicationModal, setApplicationModal] = useState<{
    open: boolean
    jobPostingId: string | null
    positionId: string | null
  }>({
    open: false,
    jobPostingId: null,
    positionId: null,
  })

  const onApply = ({ jobPostingId, positionId }: { jobPostingId: string; positionId: string }) => {
    setApplyModal({
      open: true,
      jobPostingId,
      positionId,
    })
  }

  const onCloseApplyModal = () => {
    setApplyModal({
      open: false,
      jobPostingId: null,
      positionId: null,
    })
    ampli.applyToJobExit()
  }

  const onViewApplication = ({ jobPostingId, positionId }: { jobPostingId: string; positionId: string }) => {
    setApplicationModal({
      open: true,
      jobPostingId,
      positionId,
    })
  }

  const onCloseApplicationModal = () => {
    setApplicationModal({
      open: false,
      jobPostingId: null,
      positionId: null,
    })
  }

  return (
    <>
      <ApplyToJobDrawer
        serverSession={serverSession}
        open={applyModal.open}
        jobPostingId={applyModal.jobPostingId}
        positionId={applyModal.positionId}
        onClose={onCloseApplyModal}
      />
      <AuthenticatedJobDetailsContent
        jobPosting={jobPosting}
        version="page"
        serverSession={serverSession}
        showSkeletonImage={jobPostingHasImage}
        loading={loading || !jobPosting}
        openSubscriptionCheckoutModal={openSubscriptionCheckoutModal}
      />
      <JobApplicationDrawer
        serverSession={serverSession}
        open={applicationModal.open}
        jobPostingId={applicationModal.jobPostingId}
        positionId={applicationModal.positionId}
        onClose={onCloseApplicationModal}
      />

      <div
        className="sticky bottom-6 left-0 right-0 w-full rounded-xl bg-bg-1 px-6 py-4"
        style={{
          boxShadow: "0px 8px 25px 0px rgba(0, 0, 0, 0.20)",
        }}>
        <div className="flex w-full flex-row items-center">
          <AuthenticatedJobDetailsFooter
            creativeViewerJobPosting={jobPosting}
            serverSession={serverSession}
            onApply={onApply}
            loading={loading || !jobPosting}
            onViewApplication={onViewApplication}
            openSubscriptionCheckoutModal={openSubscriptionCheckoutModal}
          />
        </div>
      </div>
    </>
  )
}
